import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';

export default function CreateEventPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;

  const [formData, setFormData] = useState({
    title: '',
    date: '',
    time: '',
    duration: '2',
    location: '',
    type: '',
    tags: [],
    capacity: '',
    price: 'free',
    priceAmount: '',
    description: '',
    imageUrl: '',
    specialInstructions: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // AI Assistant State
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [isAiGenerating, setIsAiGenerating] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [activeTooltip, setActiveTooltip] = useState('');

  // Audience State
  const [audienceSettings, setAudienceSettings] = useState({
    trentConnect: true,
    customerCommunity: true,
    socialMedia: true
  });

  const [predictedAttendance, setPredictedAttendance] = useState('12-18');
  const [eventUrl, setEventUrl] = useState('');

  const eventTypes = ['Networking', 'Workshop', 'Exhibition', 'Market', 'Class', 'Other'];
  const suggestedTags = ['#coffee', '#business', '#networking', '#workshop', '#local', '#community'];

  const userInfo = {
    name: 'The Coffee House',
    aiUsed: 2,
    aiLimit: 5,
    customerCount: 47,
    repeatCustomers: 12,
    firstTimers: 35,
    dormantCustomers: 8
  };

  const canUseAI = () => userInfo.aiUsed < userInfo.aiLimit;
  const getRemainingAIUses = () => userInfo.aiLimit - userInfo.aiUsed;

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.title.trim()) {
        newErrors.title = 'Event title is required';
      } else if (formData.title.trim().length < 5) {
        newErrors.title = 'Event title must be at least 5 characters';
      }

      if (!formData.date) {
        newErrors.date = 'Event date is required';
      } else {
        const eventDate = new Date(formData.date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (eventDate < today) {
          newErrors.date = 'Event date cannot be in the past';
        }
      }

      if (!formData.time) {
        newErrors.time = 'Event time is required';
      }

      if (!formData.location.trim()) {
        newErrors.location = 'Event location is required';
      }

      if (!formData.type) {
        newErrors.type = 'Event type is required';
      }

      if (formData.capacity && (isNaN(formData.capacity) || parseInt(formData.capacity) < 1)) {
        newErrors.capacity = 'Capacity must be a positive number';
      }

      if (formData.price === 'paid' && (!formData.priceAmount || parseFloat(formData.priceAmount) <= 0)) {
        newErrors.priceAmount = 'Please enter a valid price amount';
      }
    }

    if (step === 2) {
      if (!formData.description.trim()) {
        newErrors.description = 'Event description is required';
      } else if (formData.description.trim().length < 20) {
        newErrors.description = 'Description must be at least 20 characters';
      }

      if (formData.imageUrl && !isValidUrl(formData.imageUrl)) {
        newErrors.imageUrl = 'Please enter a valid image URL';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const addTag = (tag) => {
    if (!formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const generateAiContent = async (type = 'description') => {
    if (!canUseAI()) {
      alert(`AI assistant limit reached. ${getRemainingAIUses()} uses remaining this month.`);
      return;
    }

    try {
      setIsAiGenerating(true);
      
      let prompt = '';
      if (type === 'description') {
        prompt = aiPrompt || `${formData.type} event ${formData.title} ${formData.location}`;
      } else if (type === 'title') {
        prompt = `${formData.type} event at ${formData.location}`;
      }

      // Simulate AI generation with different responses
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const responses = {
        description: [
          `Join local professionals for casual morning networking over expertly crafted coffee. Connect with entrepreneurs, freelancers, and business leaders in a relaxed atmosphere. Perfect for making new connections and growing your network while enjoying our signature blends.`,
          `Experience an inspiring networking session designed for Nottingham's vibrant business community. Share ideas, forge partnerships, and discover opportunities in our welcoming coffee house environment.`,
          `Kickstart your day with meaningful conversations and quality connections. This informal gathering brings together like-minded professionals in a comfortable setting perfect for relationship building.`
        ],
        title: [
          `Morning Coffee & Professional Networking`,
          `Business Connect: Coffee & Conversation`,
          `Networking Brew: Start Your Day Right`,
          `Professional Coffee Meetup`
        ]
      };

      if (type === 'description') {
        setAiSuggestions(responses.description);
        setAiResponse(responses.description[0]);
      } else {
        setAiSuggestions(responses.title);
      }
    } catch (error) {
      alert('Error generating content. Please try again.');
    } finally {
      setIsAiGenerating(false);
    }
  };

  const applyAiSuggestion = (suggestion, field = 'description') => {
    setFormData(prev => ({
      ...prev,
      [field]: suggestion
    }));
    setAiResponse('');
    setAiSuggestions([]);
    setAiPrompt('');
  };

  const generateEventUrl = () => {
    const slug = formData.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    return `https://trentconnect.com/events/${slug}-${Date.now()}`;
  };

  const shareEvent = (platform) => {
    const url = eventUrl || generateEventUrl();
    const text = `Check out this event: ${formData.title} at ${formData.location}`;
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`,
      email: `mailto:?subject=${encodeURIComponent(formData.title)}&body=${encodeURIComponent(text + '\n\n' + url)}`
    };

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  const copyEventUrl = () => {
    const url = eventUrl || generateEventUrl();
    navigator.clipboard.writeText(url);
    // Add toast notification here
    alert('Event link copied to clipboard!');
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      if (currentStep === 2 && !eventUrl) {
        setEventUrl(generateEventUrl());
      }
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      return;
    }

    try {
      setIsSubmitting(true);
      // Simulate event creation
      await new Promise(resolve => setTimeout(resolve, 2000));
      navigate('/dashboard');
    } catch (error) {
      setErrors({ submit: 'Failed to create event. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1: return 'Event Details';
      case 2: return 'Description & Media';
      case 3: return 'Promotion & Review';
      default: return 'Event Details';
    }
  };

  const Tooltip = ({ id, content, children }) => (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setActiveTooltip(id)}
        onMouseLeave={() => setActiveTooltip('')}
      >
        {children}
      </div>
      {activeTooltip === id && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 text-white text-sm rounded-lg whitespace-nowrap z-50">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-slate-900"></div>
        </div>
      )}
    </div>
  );

  return (
    <DashboardLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center text-slate-600 hover:text-slate-900 transition-colors"
            >
              <i className="fas fa-arrow-left mr-2"></i>
              Back to Dashboard
            </button>
          </div>
          
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Create New Event</h1>
              <p className="text-slate-600">
                Share your event with the Nottingham community and build connections
              </p>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-900">Step {currentStep} of {totalSteps}: {getStepTitle()}</span>
              <span className="text-sm text-slate-600">{Math.round((currentStep / totalSteps) * 100)}% Complete</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-blue-600 to-purple-600 h-2 rounded-full transition-all duration-300" 
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              ></div>
            </div>
            <div className="flex justify-between mt-2">
              {[...Array(totalSteps)].map((_, index) => (
                <div 
                  key={index}
                  className={`w-3 h-3 rounded-full ${
                    index < currentStep ? 'bg-blue-600' : 'bg-slate-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Step Content */}
        <div className="max-w-4xl mx-auto">
          {/* Step 1: Event Details */}
          {currentStep === 1 && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-slate-900">Event Details</h2>
                  <div className="flex items-center space-x-2">
                    <Tooltip id="ai-help" content="Use AI to generate amazing event titles and content">
                      <button
                        onClick={() => generateAiContent('title')}
                        disabled={isAiGenerating || !canUseAI()}
                        className="flex items-center bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:from-slate-400 disabled:to-slate-400 text-white px-3 py-2 rounded-lg text-sm font-medium transition-all disabled:cursor-not-allowed"
                      >
                        <i className="fas fa-sparkles mr-2"></i>
                        AI Assist ({getRemainingAIUses()})
                      </button>
                    </Tooltip>
                  </div>
                </div>
              </div>
              <div className="p-6 space-y-6">
                {/* AI Title Suggestions */}
                {aiSuggestions.length > 0 && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-purple-900 mb-3">
                      <i className="fas fa-sparkles mr-2"></i>
                      AI-Generated Event Titles
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {aiSuggestions.map((title, index) => (
                        <button
                          key={index}
                          onClick={() => applyAiSuggestion(title, 'title')}
                          className="text-left p-3 bg-white border border-purple-200 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors"
                        >
                          <span className="text-sm font-medium text-slate-900">{title}</span>
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => setAiSuggestions([])}
                      className="mt-3 text-sm text-purple-600 hover:text-purple-800"
                    >
                      <i className="fas fa-times mr-1"></i>
                      Dismiss suggestions
                    </button>
                  </div>
                )}

                {/* Event Title */}
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-slate-700 mb-2">
                    Event Title *
                    <Tooltip id="title-tip" content="A compelling title increases attendance by 40%">
                      <i className="fas fa-info-circle ml-2 text-slate-400 cursor-help"></i>
                    </Tooltip>
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-3 border rounded-lg bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.title ? 'border-red-500' : 'border-slate-300'
                    }`}
                    placeholder="e.g., Morning Coffee & Networking Session"
                  />
                  {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
                </div>

                {/* Date, Time, Duration */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="date" className="block text-sm font-medium text-slate-700 mb-2">
                      Event Date *
                      <Tooltip id="date-tip" content="Weekends typically see 30% higher attendance">
                        <i className="fas fa-info-circle ml-2 text-slate-400 cursor-help"></i>
                      </Tooltip>
                    </label>
                    <input
                      type="date"
                      id="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      min={new Date().toISOString().split('T')[0]}
                      className={`w-full px-3 py-3 border rounded-lg bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.date ? 'border-red-500' : 'border-slate-300'
                      }`}
                    />
                    {errors.date && <p className="mt-1 text-sm text-red-600">{errors.date}</p>}
                  </div>

                  <div>
                    <label htmlFor="time" className="block text-sm font-medium text-slate-700 mb-2">
                      Event Time *
                      <Tooltip id="time-tip" content="Morning events (9-11 AM) have highest attendance rates">
                        <i className="fas fa-info-circle ml-2 text-slate-400 cursor-help"></i>
                      </Tooltip>
                    </label>
                    <input
                      type="time"
                      id="time"
                      name="time"
                      value={formData.time}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-3 border rounded-lg bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.time ? 'border-red-500' : 'border-slate-300'
                      }`}
                    />
                    {errors.time && <p className="mt-1 text-sm text-red-600">{errors.time}</p>}
                  </div>

                  <div>
                    <label htmlFor="duration" className="block text-sm font-medium text-slate-700 mb-2">
                      Duration (hours)
                    </label>
                    <select
                      id="duration"
                      name="duration"
                      value={formData.duration}
                      onChange={handleInputChange}
                      className="w-full px-3 py-3 border border-slate-300 rounded-lg bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="1">1 hour</option>
                      <option value="2">2 hours</option>
                      <option value="3">3 hours</option>
                      <option value="4">4 hours</option>
                      <option value="6">6 hours</option>
                      <option value="8">All day</option>
                    </select>
                  </div>
                </div>

                {/* Location */}
                <div>
                  <label htmlFor="location" className="block text-sm font-medium text-slate-700 mb-2">
                    Location *
                    <Tooltip id="location-tip" content="Include postcode for better discovery">
                      <i className="fas fa-info-circle ml-2 text-slate-400 cursor-help"></i>
                    </Tooltip>
                  </label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-3 border rounded-lg bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.location ? 'border-red-500' : 'border-slate-300'
                    }`}
                    placeholder="The Coffee House, 123 High St, Nottingham NG1 2AB"
                  />
                  {errors.location && <p className="mt-1 text-sm text-red-600">{errors.location}</p>}
                  <p className="mt-1 text-xs text-slate-500">Include full address for better discovery</p>
                </div>

                {/* Event Type, Capacity, and Price */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="type" className="block text-sm font-medium text-slate-700 mb-2">
                      Event Type *
                    </label>
                    <select
                      id="type"
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-3 border rounded-lg bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.type ? 'border-red-500' : 'border-slate-300'
                      }`}
                    >
                      <option value="">Select event type</option>
                      {eventTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                    {errors.type && <p className="mt-1 text-sm text-red-600">{errors.type}</p>}
                  </div>

                  <div>
                    <label htmlFor="capacity" className="block text-sm font-medium text-slate-700 mb-2">
                      Max Capacity
                      <Tooltip id="capacity-tip" content="Events with capacity limits create urgency and higher conversions">
                        <i className="fas fa-info-circle ml-2 text-slate-400 cursor-help"></i>
                      </Tooltip>
                    </label>
                    <input
                      type="number"
                      id="capacity"
                      name="capacity"
                      value={formData.capacity}
                      onChange={handleInputChange}
                      min="1"
                      className={`w-full px-3 py-3 border rounded-lg bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.capacity ? 'border-red-500' : 'border-slate-300'
                      }`}
                      placeholder="e.g., 25"
                    />
                    {errors.capacity && <p className="mt-1 text-sm text-red-600">{errors.capacity}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Event Price *
                      <Tooltip id="price-tip" content="Free events get 5x more attendees but paid events have higher engagement">
                        <i className="fas fa-info-circle ml-2 text-slate-400 cursor-help"></i>
                      </Tooltip>
                    </label>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="price-free"
                          name="price"
                          value="free"
                          checked={formData.price === 'free'}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-blue-600 border-slate-300 focus:ring-blue-500"
                        />
                        <label htmlFor="price-free" className="text-sm font-medium text-slate-900">
                          Free Event
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="price-paid"
                          name="price"
                          value="paid"
                          checked={formData.price === 'paid'}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-blue-600 border-slate-300 focus:ring-blue-500"
                        />
                        <label htmlFor="price-paid" className="text-sm font-medium text-slate-900">
                          Paid Event
                        </label>
                      </div>
                      {formData.price === 'paid' && (
                        <div className="ml-6">
                          <div className="flex items-center space-x-2">
                            <span className="text-slate-700">£</span>
                            <input
                              type="number"
                              name="priceAmount"
                              value={formData.priceAmount}
                              onChange={handleInputChange}
                              min="0.01"
                              step="0.01"
                              className={`w-24 px-2 py-1 border rounded text-sm ${
                                errors.priceAmount ? 'border-red-500' : 'border-slate-300'
                              }`}
                              placeholder="5.00"
                            />
                            <span className="text-slate-600 text-sm">per person</span>
                          </div>
                          {errors.priceAmount && <p className="mt-1 text-sm text-red-600">{errors.priceAmount}</p>}
                          <p className="mt-1 text-xs text-slate-500">
                            Payments processed via Stripe. Funds deposited within 2 business days.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Category Tags */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Category Tags
                    <Tooltip id="tags-tip" content="Events with 3-5 tags get 25% more discovery">
                      <i className="fas fa-info-circle ml-2 text-slate-400 cursor-help"></i>
                    </Tooltip>
                  </label>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {suggestedTags.map(tag => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => addTag(tag)}
                        disabled={formData.tags.includes(tag)}
                        className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                          formData.tags.includes(tag)
                            ? 'bg-blue-100 text-blue-800 cursor-not-allowed'
                            : 'bg-slate-100 text-slate-700 hover:bg-blue-50 hover:text-blue-700'
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                  {formData.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.tags.map(tag => (
                        <div key={tag} className="flex items-center bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                          <span>{tag}</span>
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            className="ml-2 text-blue-600 hover:text-blue-800"
                          >
                            <i className="fas fa-times text-xs"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Description & Media */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
                <div className="p-6 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-slate-900">Event Description & Media</h2>
                    <div className="flex items-center space-x-3">
                      <Tooltip id="ai-content" content="Generate professional descriptions that convert browsers to attendees">
                        <button
                          onClick={() => generateAiContent('description')}
                          disabled={isAiGenerating || !canUseAI()}
                          className="flex items-center bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:from-slate-400 disabled:to-slate-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:cursor-not-allowed"
                        >
                          <i className="fas fa-magic mr-2"></i>
                          {isAiGenerating ? 'Generating...' : `AI Generate (${getRemainingAIUses()})`}
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  {/* AI Input for Description */}
                  <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-slate-900 mb-3">
                      <i className="fas fa-sparkles mr-2 text-purple-600"></i>
                      AI Content Generator
                    </h3>
                    <p className="text-sm text-slate-600 mb-4">
                      Describe your event in keywords and let AI craft compelling content that attracts attendees.
                    </p>
                    
                    <div className="space-y-4">
                      <div className="flex space-x-3">
                        <input
                          type="text"
                          value={aiPrompt}
                          onChange={(e) => setAiPrompt(e.target.value)}
                          placeholder="e.g., coffee networking morning professionals friendly atmosphere business"
                          className="flex-1 px-3 py-3 border border-purple-300 rounded-lg bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                        <button
                          onClick={() => generateAiContent('description')}
                          disabled={isAiGenerating || !canUseAI()}
                          className="bg-purple-600 hover:bg-purple-700 disabled:bg-slate-400 text-white px-6 py-3 rounded-lg transition-colors disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          {isAiGenerating ? (
                            <span className="flex items-center">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Generating...
                            </span>
                          ) : (
                            <>
                              <i className="fas fa-magic mr-2"></i>
                              Generate
                            </>
                          )}
                        </button>
                      </div>

                      {/* AI Generated Content */}
                      {(aiResponse || aiSuggestions.length > 0) && (
                        <div className="bg-white border border-purple-300 rounded-lg p-4">
                          <h4 className="text-sm font-semibold text-purple-900 mb-3">
                            <i className="fas fa-sparkles mr-2"></i>
                            AI-Generated Descriptions
                          </h4>
                          
                          {aiResponse && (
                            <div className="mb-4">
                              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-3">
                                <p className="text-slate-700 leading-relaxed">{aiResponse}</p>
                              </div>
                              <div className="flex space-x-3">
                                <button
                                  onClick={() => applyAiSuggestion(aiResponse, 'description')}
                                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                                >
                                  <i className="fas fa-check mr-2"></i>
                                  Use This Description
                                </button>
                                <button
                                  onClick={() => generateAiContent('description')}
                                  disabled={isAiGenerating}
                                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                                >
                                  <i className="fas fa-refresh mr-2"></i>
                                  Generate Another
                                </button>
                              </div>
                            </div>
                          )}

                          {aiSuggestions.length > 0 && (
                            <div className="space-y-2">
                              <h5 className="text-sm font-medium text-slate-700">Alternative Versions:</h5>
                              {aiSuggestions.slice(1).map((suggestion, index) => (
                                <div key={index} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                                  <p className="text-slate-700 text-sm mb-2">{suggestion}</p>
                                  <button
                                    onClick={() => applyAiSuggestion(suggestion, 'description')}
                                    className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                                  >
                                    <i className="fas fa-plus mr-1"></i>
                                    Use This Version
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}

                          <button
                            onClick={() => {
                              setAiResponse('');
                              setAiSuggestions([]);
                              setAiPrompt('');
                            }}
                            className="mt-3 text-sm text-slate-600 hover:text-slate-800"
                          >
                            <i className="fas fa-times mr-1"></i>
                            Clear suggestions
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-slate-700 mb-2">
                      Event Description *
                      <Tooltip id="desc-tip" content="Descriptions with 50-150 words get the best engagement">
                        <i className="fas fa-info-circle ml-2 text-slate-400 cursor-help"></i>
                      </Tooltip>
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={6}
                      className={`w-full px-3 py-3 border rounded-lg bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
                        errors.description ? 'border-red-500' : 'border-slate-300'
                      }`}
                      placeholder="Describe your event in detail. What can attendees expect? What should they bring? Any special requirements?"
                    />
                    {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
                    <div className="flex justify-between mt-1">
                      <p className="text-xs text-slate-500">
                        Minimum 20 characters. Be descriptive to attract more attendees!
                      </p>
                      <span className="text-xs text-slate-500">
                        {formData.description.length} characters
                      </span>
                    </div>
                  </div>

                  {/* Event Image */}
                  <div>
                    <label htmlFor="imageUrl" className="block text-sm font-medium text-slate-700 mb-2">
                      Event Image URL
                      <Tooltip id="image-tip" content="Events with images get 3x more clicks and bookings">
                        <i className="fas fa-info-circle ml-2 text-slate-400 cursor-help"></i>
                      </Tooltip>
                    </label>
                    <input
                      type="url"
                      id="imageUrl"
                      name="imageUrl"
                      value={formData.imageUrl}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-3 border rounded-lg bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.imageUrl ? 'border-red-500' : 'border-slate-300'
                      }`}
                      placeholder="https://example.com/image.jpg"
                    />
                    {errors.imageUrl && <p className="mt-1 text-sm text-red-600">{errors.imageUrl}</p>}
                    <p className="mt-1 text-xs text-slate-500">
                      Add a compelling image to make your event stand out. Recommended: 1200x630px
                    </p>
                    {formData.imageUrl && isValidUrl(formData.imageUrl) && (
                      <div className="mt-3">
                        <img 
                          src={formData.imageUrl} 
                          alt="Event preview" 
                          className="w-full max-w-md h-32 object-cover rounded-lg border border-slate-200"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Special Instructions */}
                  <div>
                    <label htmlFor="specialInstructions" className="block text-sm font-medium text-slate-700 mb-2">
                      Special Instructions
                      <Tooltip id="instructions-tip" content="Clear instructions reduce no-shows by 20%">
                        <i className="fas fa-info-circle ml-2 text-slate-400 cursor-help"></i>
                      </Tooltip>
                    </label>
                    <textarea
                      id="specialInstructions"
                      name="specialInstructions"
                      value={formData.specialInstructions}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-3 py-3 border border-slate-300 rounded-lg bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      placeholder="What should attendees bring? Any requirements? Dress code? Parking info?"
                    />
                    <p className="mt-1 text-xs text-slate-500">
                      Help attendees prepare for the best experience
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Promotion & Review */}
          {currentStep === 3 && (
            <div className="space-y-6">
              {/* Audience Settings */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
                <div className="p-6 border-b border-slate-200">
                  <h2 className="text-lg font-semibold text-slate-900">Promotion & Sharing</h2>
                </div>
                <div className="p-6 space-y-6">
                  {/* Audience Selection */}
                  <div>
                    <h3 className="text-base font-semibold text-slate-900 mb-4">Who Should Know About This Event?</h3>
                    <div className="space-y-4">
                      {/* TrentConnect Community */}
                      <div className="flex items-start space-x-4 p-4 border border-slate-200 rounded-lg">
                        <div className="flex items-center h-5">
                          <input
                            type="checkbox"
                            checked={audienceSettings.trentConnect}
                            onChange={(e) => setAudienceSettings(prev => ({
                              ...prev,
                              trentConnect: e.target.checked
                            }))}
                            className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                          />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <i className="fas fa-globe text-blue-600"></i>
                            <h4 className="text-base font-semibold text-slate-900">TrentConnect Community</h4>
                          </div>
                          <p className="text-sm text-slate-600 mb-2">
                            Visible to all Nottingham event browsers
                          </p>
                          <p className="text-sm font-medium text-blue-600">
                            Expected reach: ~500-800 people
                          </p>
                        </div>
                      </div>

                      {/* Customer Community */}
                      <div className="flex items-start space-x-4 p-4 border border-slate-200 rounded-lg">
                        <div className="flex items-center h-5">
                          <input
                            type="checkbox"
                            checked={audienceSettings.customerCommunity}
                            onChange={(e) => setAudienceSettings(prev => ({
                              ...prev,
                              customerCommunity: e.target.checked
                            }))}
                            className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                          />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <i className="fas fa-envelope text-purple-600"></i>
                            <h4 className="text-base font-semibold text-slate-900">Your Customer Community ({userInfo.customerCount} people)</h4>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                            <div className="flex items-center justify-between text-sm">
                              <span className="flex items-center text-slate-600">
                                <i className="fas fa-redo text-blue-600 mr-2"></i>
                                {userInfo.repeatCustomers} repeat
                              </span>
                              <span className="text-green-600 font-medium">High rate</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="flex items-center text-slate-600">
                                <i className="fas fa-star text-purple-600 mr-2"></i>
                                {userInfo.firstTimers} first-time
                              </span>
                              <span className="text-yellow-600 font-medium">Moderate rate</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="flex items-center text-slate-600">
                                <i className="fas fa-sleep text-slate-400 mr-2"></i>
                                {userInfo.dormantCustomers} dormant
                              </span>
                              <span className="text-red-600 font-medium">Low rate</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Social Media */}
                      <div className="flex items-start space-x-4 p-4 border border-slate-200 rounded-lg">
                        <div className="flex items-center h-5">
                          <input
                            type="checkbox"
                            checked={audienceSettings.socialMedia}
                            onChange={(e) => setAudienceSettings(prev => ({
                              ...prev,
                              socialMedia: e.target.checked
                            }))}
                            className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                          />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <i className="fas fa-share text-green-600"></i>
                            <h4 className="text-base font-semibold text-slate-900">Social Media Ready</h4>
                          </div>
                          <p className="text-sm text-slate-600">
                            Generate shareable graphics and links for all your platforms
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Predicted Attendance */}
                    <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <i className="fas fa-chart-line text-blue-600"></i>
                        <h4 className="text-sm font-semibold text-blue-900">Predicted attendance: {predictedAttendance} people</h4>
                      </div>
                      <p className="text-sm text-blue-700">
                        💡 Tip: Your repeat customers are 3x more likely to attend
                      </p>
                    </div>
                  </div>

                  {/* Instant Sharing */}
                  <div>
                    <h3 className="text-base font-semibold text-slate-900 mb-4">Instant Share</h3>
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="text-sm font-medium text-slate-900">Event Link</h4>
                          <p className="text-xs text-slate-600">Share this link across all platforms</p>
                        </div>
                        <button
                          onClick={copyEventUrl}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm transition-colors"
                        >
                          <i className="fas fa-copy mr-1"></i>
                          Copy Link
                        </button>
                      </div>
                      
                      <div className="bg-white border border-slate-300 rounded p-2 mb-4">
                        <code className="text-xs text-slate-700 break-all">
                          {eventUrl || generateEventUrl()}
                        </code>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                        <button
                          onClick={() => shareEvent('facebook')}
                          className="flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm transition-colors"
                        >
                          <i className="fab fa-facebook"></i>
                          <span className="hidden md:inline">Facebook</span>
                        </button>
                        <button
                          onClick={() => shareEvent('twitter')}
                          className="flex items-center justify-center space-x-2 bg-blue-400 hover:bg-blue-500 text-white px-3 py-2 rounded text-sm transition-colors"
                        >
                          <i className="fab fa-twitter"></i>
                          <span className="hidden md:inline">Twitter</span>
                        </button>
                        <button
                          onClick={() => shareEvent('linkedin')}
                          className="flex items-center justify-center space-x-2 bg-blue-700 hover:bg-blue-800 text-white px-3 py-2 rounded text-sm transition-colors"
                        >
                          <i className="fab fa-linkedin"></i>
                          <span className="hidden md:inline">LinkedIn</span>
                        </button>
                        <button
                          onClick={() => shareEvent('whatsapp')}
                          className="flex items-center justify-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded text-sm transition-colors"
                        >
                          <i className="fab fa-whatsapp"></i>
                          <span className="hidden md:inline">WhatsApp</span>
                        </button>
                        <button
                          onClick={() => shareEvent('email')}
                          className="flex items-center justify-center space-x-2 bg-slate-600 hover:bg-slate-700 text-white px-3 py-2 rounded text-sm transition-colors"
                        >
                          <i className="fas fa-envelope"></i>
                          <span className="hidden md:inline">Email</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Event Preview */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
                <div className="p-6 border-b border-slate-200">
                  <h2 className="text-lg font-semibold text-slate-900">Review Your Event</h2>
                </div>
                <div className="p-6">
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
                    {formData.imageUrl && (
                      <div className="mb-4">
                        <img 
                          src={formData.imageUrl} 
                          alt="Event preview" 
                          className="w-full h-48 object-cover rounded-lg"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{formData.title}</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center text-slate-600">
                        <i className="fas fa-calendar mr-3 text-blue-600"></i>
                        <span>
                          {formData.date && new Date(formData.date).toLocaleDateString('en-GB', { 
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </span>
                      </div>
                      <div className="flex items-center text-slate-600">
                        <i className="fas fa-clock mr-3 text-blue-600"></i>
                        <span>{formData.time} • {formData.duration} hours</span>
                      </div>
                      <div className="flex items-center text-slate-600">
                        <i className="fas fa-map-marker-alt mr-3 text-blue-600"></i>
                        <span>{formData.location}</span>
                      </div>
                      <div className="flex items-center text-slate-600">
                        <i className="fas fa-tag mr-3 text-blue-600"></i>
                        <span>{formData.type}</span>
                      </div>
                      {formData.capacity && (
                        <div className="flex items-center text-slate-600">
                          <i className="fas fa-users mr-3 text-blue-600"></i>
                          <span>{formData.capacity} capacity</span>
                        </div>
                      )}
                      <div className="flex items-center text-slate-600">
                        <i className="fas fa-pound-sign mr-3 text-blue-600"></i>
                        <span className={`font-semibold ${formData.price === 'free' ? 'text-green-600' : 'text-blue-600'}`}>
                          {formData.price === 'free' ? 'Free' : `£${formData.priceAmount}`}
                        </span>
                      </div>
                    </div>

                    {formData.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {formData.tags.map(tag => (
                          <span key={tag} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="mb-4">
                      <h4 className="font-semibold text-slate-900 mb-2">About this event</h4>
                      <p className="text-slate-700 leading-relaxed">{formData.description}</p>
                    </div>

                    {formData.specialInstructions && (
                      <div className="mb-4">
                        <h4 className="font-semibold text-slate-900 mb-2">Special Instructions</h4>
                        <p className="text-slate-700">{formData.specialInstructions}</p>
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                      <span className="text-sm text-slate-600">Hosted by {userInfo.name}</span>
                      <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors">
                        {formData.price === 'free' ? 'RSVP Now' : `Book Now - £${formData.priceAmount}`}
                      </button>
                    </div>
                  </div>

                  {/* Promotion Summary */}
                  <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">📊 Promotion Summary:</h4>
                    <ul className="space-y-1 text-sm text-blue-700">
                      {audienceSettings.trentConnect && (
                        <li>• TrentConnect platform (500-800 reach)</li>
                      )}
                      {audienceSettings.customerCommunity && (
                        <li>• Your {userInfo.customerCount} customers ({predictedAttendance} expected attendance)</li>
                      )}
                      {audienceSettings.socialMedia && (
                        <li>• Social media graphics and shareable links included</li>
                      )}
                    </ul>
                  </div>

                  {/* Payment Processing Info */}
                  {formData.price === 'paid' && (
                    <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
                      <h4 className="font-semibold text-green-900 mb-2">💳 Payment Processing:</h4>
                      <ul className="space-y-1 text-sm text-green-700">
                        <li>• Secure payments processed via Stripe</li>
                        <li>• Funds deposited to your account within 2 business days</li>
                        <li>• 2.9% + 30p processing fee per transaction</li>
                        <li>• Automatic email receipts sent to attendees</li>
                      </ul>
                    </div>
                  )}

                  {/* Important Notice */}
                  <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start space-x-2">
                      <i className="fas fa-exclamation-triangle text-amber-600 mt-0.5"></i>
                      <div>
                        <h4 className="font-semibold text-amber-900 mb-1">Important:</h4>
                        <p className="text-sm text-amber-800">
                          Once published, customer notifications are sent immediately. You can edit details afterwards, but payment settings cannot be changed once bookings are received.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center pt-6">
            {currentStep > 1 ? (
              <button
                onClick={prevStep}
                className="flex items-center px-6 py-3 border border-slate-300 text-slate-700 hover:text-slate-900 hover:border-slate-400 rounded-lg font-medium transition-colors"
              >
                <i className="fas fa-arrow-left mr-2"></i>
                Previous
              </button>
            ) : (
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center px-6 py-3 border border-slate-300 text-slate-700 hover:text-slate-900 hover:border-slate-400 rounded-lg font-medium transition-colors"
              >
                <i className="fas fa-times mr-2"></i>
                Cancel
              </button>
            )}

            {currentStep < totalSteps ? (
              <button
                onClick={nextStep}
                className="flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-medium transition-all transform hover:scale-105 shadow-lg"
              >
                Continue
                <i className="fas fa-arrow-right ml-2"></i>
              </button>
            ) : (
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    // Save as draft logic here
                    navigate('/dashboard');
                  }}
                  className="px-6 py-3 border border-slate-300 text-slate-700 hover:text-slate-900 hover:border-slate-400 rounded-lg font-medium transition-colors"
                >
                  Save as Draft
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-slate-400 disabled:to-slate-400 text-white rounded-lg font-medium transition-all transform hover:scale-105 disabled:transform-none disabled:cursor-not-allowed shadow-lg"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Publishing Event...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-rocket mr-2"></i>
                      Publish Event
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Error Display */}
          {errors.submit && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <i className="fas fa-exclamation-circle text-red-600"></i>
                <p className="text-sm text-red-800">{errors.submit}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}